import './ccf-translator.module.scss';
import React, { useContext } from 'react';
import { translations } from '@nice-ccf/i18n';


export type CcfTranslatorProviderType = typeof translations;
export type CcfTranslationKey = any;
export type CcfTranslations = typeof translations.text;

export interface CcfTranslatorProps {
  translationKey?: CcfTranslationKey;
  children?: React.ReactNode;
  extraArgs?: { format: (number | string)[] };
}

export interface withTranslationProps {
  translator: (input: CcfTranslationKey ) => string,
  locale: 'en' | 'he';
  translationKey?: CcfTranslationKey
}

export const TranslatorContext = React.createContext(translations);

/**
 * Returns Translator Context with provided translations value.
 *
 * @remarks
 * This method is part of the react-ui-controls.
 *
 * @param translations - translation configuration.
 * @param children - React Node Elements.
 * @example <CcfTranslatorProvider />
 * @returns Translator Context Provider with provided translations configurations.
 *
 */
export function CcfTranslatorProvider(props: {
  translations: CcfTranslatorProviderType;
  children: React.ReactNode;
}) {
  return (
    <TranslatorContext.Provider value={props.translations}>
      {props.children}
    </TranslatorContext.Provider>
  );
}

/**
 * Returns Translated Text for Provided Input Text.
 *
 * @remarks
 * This method is part of the react-ui-controls.
 *
 * @param inputText - Text to be Translated.
 * @example useTranslator()
 * @returns Translated Text for Provided Input Text.
 *
 */
export function useTranslator() {
  const {text,locale} = useContext(TranslatorContext);
  /**
   * 
   * @param input - CcfTranslationKey
   * @param extraArgs - any
   * @example
   * @returns 
   */
  const translate = (input: CcfTranslationKey, extraArgs? : { format : (string | number)[] }) => {
    let translatedText = (text as any)[input];
    if(extraArgs) {
      for (let i = 0; i < extraArgs.format.length; i++) {
        const regexp = new RegExp('\\{x\\}', 'gi');
        translatedText = translatedText.replace(regexp, extraArgs.format[i].toLocaleString(locale) );
      }
    }
    return translatedText;
  };
  return [translate];
}

/**
 * Returns Higher Order Component for Provided Wrapper Component with Translated Text.
 *
 * @remarks
 * This method is part of the react-ui-controls.
 *
 * @param WrapperComponent - Component whose child text elements to be translated to provided locale.
 * @example withTranslation
 * @returns HOC with Translated Text for provided Wrapper Component.
 *
 */
export function withTranslation<T>(
  WrapperComponent: React.ComponentType<T>
): React.FC<Omit<T, keyof withTranslationProps> & CcfTranslatorProps> {
  
  /**
  * 
  * @param param0 - CcfTranslatorProps
  * @example - TranslatedComponent
  * @returns 
  */
  const TranslatedComponent: React.FC<
    Omit<T, keyof withTranslationProps> & CcfTranslatorProps
  > = ({
    translationKey,
    extraArgs,
    children,
    ...rest
  }: CcfTranslatorProps) => {
    const { text , locale } = React.useContext(TranslatorContext);

    /**
     * 
     * @param input - CcfTranslationKey
     * @example translator()
     * @returns 
     */
    const translator = (input: any) => {
      let translatedText = (text as any)[input] || input ;
      if(extraArgs) {
        for (let i = 0; i < extraArgs.format.length; i++) {
          const regexp = new RegExp('\\{x\\}', 'gi');
          translatedText = translatedText.replace(regexp, extraArgs.format[i].toLocaleString(locale) );
        }
      }
      return translatedText;
    }
    return (
      <WrapperComponent {...(rest as T)} translator={translator} locale={locale} translationKey={translationKey} children={children} />
    );
  };
  return TranslatedComponent;
}
